import React, { useRef, useState, useEffect } from "react";
import { ArrorwBottom } from "icons/sidebar/ArrorwBottom";
import { UserIcon } from "icons/sidebar/UserIcon";
import { LogOutIcon } from "icons/sidebar/LogOutIcon";
import { Menu, Transition } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { guardedNavigateGlobal } from "hooks/useNavigationGuard";
import { useIsLogin, useIsSmallSidebar } from "store/slices/commonSlice";
import { useUserData } from "store/slices/authSlice";
import { clearSlice, getInitialsLatters } from "utils/HelperFunctions";
import { useAppDispatch } from "store/hooks";
import { TOKEN_STORAGE_KEY } from "store/api/axiosApi";
import Skeleton from "react-loading-skeleton";
import useOutsideClick from "hooks/useOutsideClick";
import UserImg from "icons/auth/user.svg";
import { PROFILE_URL } from "store/api/constApi";
import { useTid } from "utils/testid";

const ProfileButton = ({ cluster }: { cluster: string }) => {
  const tid = useTid();
  const [rotate, setRotate] = useState<boolean>(false);
  const [isLoading] = useState<boolean>(false);
  const isSmallSidebar = useIsSmallSidebar();
  const isLogin = useIsLogin();
  const userData = useUserData();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isImgErr, setIsImgErr] = useState<boolean>(false);

  const menuRef = useRef(null);

  const handleLogOut = () => {
    clearSlice(dispatch);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.clear();
    navigate(`/${cluster}/login`, { replace: true });
  };

  useOutsideClick(menuRef, () => {
    setRotate(false);
  });

  useEffect(() => {
    const handler = (e: Event) => {
      try {
        setIsImgErr(false);
        const detail: any = (e as CustomEvent)?.detail || {};
        const id = detail.id || userData?.data?.accountData?.id;
        const el = document.getElementById(
          "sidemenu-profile-image-1"
        ) as HTMLImageElement | null;
        if (el) {
          el.src = `${PROFILE_URL}/contact-assets/${id}?t=${Date.now()}`;
        }
      } catch (err) {
        console.log("profile-image-updated handler err ======", err);
      }
    };

    window.addEventListener("profile-image-updated", handler as EventListener);
    return () =>
      window.removeEventListener(
        "profile-image-updated",
        handler as EventListener
      );
  }, [userData]);

  const handleItemClick = (path: string) => {
    guardedNavigateGlobal(navigate, path);
    setRotate(false);
  };

  return (
    <div className={`relative w-full`}>
      <div className="w-full">
        {isLogin ? (
          <Menu as="div" className="w-full" ref={menuRef}>
            <Menu.Button
              className="w-full"
              data-testid={tid("sidebar-profile-menu-button")}
              aria-label="Open profile menu"
              onClick={() => {
                setRotate((prev) => !prev);
              }}
            >
              <div
                className={`w-full transition-all duration-300 ease-in-out flex items-center gap-3 justify-between flex-shrink-0 relative pt-4 pb-6 px-3 border-t border-strokePrimary ${
                  rotate ? "" : "hover:!bg-bgHover"
                }`}
              >
                <div className="flex items-center gap-3 ">
                  {isLoading ? (
                    <Skeleton
                      circle
                      width={isSmallSidebar ? 25 : 40}
                      height={isSmallSidebar ? 25 : 40}
                    />
                  ) : (
                    <>
                      {!isImgErr ? (
                        <div
                          className={`transition-all ease-in-out rounded-full ${
                            isSmallSidebar
                              ? "w-[25px] h-[25px] text-xs"
                              : "w-[40px] h-[40px] text-sm"
                          } cursor-pointer flex justify-center items-center overflow-clip`}
                        >
                          <img
                            id="sidemenu-profile-image-1"
                            className="block h-full w-full rounded-full object-cover object-center"
                            src={`${PROFILE_URL}/contact-assets/${
                              userData?.data?.accountData?.id
                            }?t=${Date.now()}`}
                            onError={(e) => {
                              setIsImgErr(true);
                            }}
                            alt="Profile"
                          />{" "}
                        </div>
                      ) : (
                        <div
                          className={`transition-all ease-in-out rounded-full ${
                            isSmallSidebar
                              ? "w-[25px] h-[25px] text-xs"
                              : "w-[40px] h-[40px] text-sm"
                          } cursor-pointer flex justify-center items-center bg-bgSecondary text-iconBright`}
                        >
                          {isLogin &&
                          (userData?.data?.accountData?.first_name ||
                            userData?.data?.accountData?.last_name) ? (
                            getInitialsLatters(
                              userData?.data?.accountData?.first_name,
                              userData?.data?.accountData?.last_name
                            )
                          ) : (
                            <img src={UserImg} alt="" aria-hidden="true" />
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {!isSmallSidebar ? (
                    <div className="flex flex-col items-start max-w-[155px]">
                      <label
                        title={
                          isLogin
                            ? (userData?.data?.accountData?.first_name || "") +
                              " " +
                              (userData?.data?.accountData?.last_name || "")
                            : "--"
                        }
                        className="max-w-[155px] text-txtPrimary font-medium leading-5 text-sm truncate overflow-hidden whitespace-nowrap text-ellipsis"
                      >
                        {isLogin
                          ? (userData?.data?.accountData?.first_name || "") +
                            " " +
                            (userData?.data?.accountData?.last_name || "")
                          : "--"}
                      </label>
                      <div
                        title={userData?.data?.accountData?.accountid}
                        className="max-w-[155px] flex items-center gap-1 text-sm leading-5 font-normal text-txtSecondary truncate overflow-hidden whitespace-nowrap text-ellipsis"
                      >
                        <span>ID</span>
                        <span>
                          {isLogin
                            ? userData?.data?.accountData?.accountid
                            : "--"}
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className={rotate ? "rotate-180" : "rotate-0"}>
                  <ArrorwBottom
                    aria-hidden="true"
                    className="group"
                    _className="group-hover:!fill-iconObscure"
                  />
                </div>
              </div>
            </Menu.Button>
            <Transition
              show={rotate}
              as={React.Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items
                className="absolute rounded-[12px] pt-[8px] z-10 bottom-[80px] left-[6px] mt-2 w-[240px] origin-top-right divide-y divide-gray-100 bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                data-testid={tid("sidebar-profile-menu")}
              >
                <div className="py-1">
                  <Menu.Item
                    as="div"
                    className="px-4 py-3 border-b border-strokePrimary-200"
                  >
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {isLoading ? (
                        <Skeleton circle width={40} height={40} />
                      ) : (
                        <>
                          {!isImgErr ? (
                            <div
                              className={`transition-all ease-in-out rounded-full w-[40px] h-[40px] text-xs cursor-pointer flex justify-center items-center overflow-clip`}
                            >
                              <img
                                id="sidemenu-profile-image-2"
                                className="block h-full w-full rounded-full object-cover object-center"
                                src={`${PROFILE_URL}/contact-assets/${
                                  userData?.data?.accountData?.id
                                }?t=${Date.now()}`}
                                onError={(e) => {
                                  setIsImgErr(true);
                                  // setProfile((state) => ({ ...state, isAdded: false }));
                                }}
                                alt="Profile"
                              />{" "}
                            </div>
                          ) : (
                            <div
                              className={`transition-all ease-in-out rounded-full w-[40px] h-[40px] text-xs cursor-pointer flex justify-center items-center bg-bgSecondary text-iconBright`}
                            >
                              {isLogin &&
                              (userData?.data?.accountData?.first_name ||
                                userData?.data?.accountData?.last_name) ? (
                                getInitialsLatters(
                                  userData?.data?.accountData?.first_name,
                                  userData?.data?.accountData?.last_name
                                )
                              ) : (
                                <img src={UserImg} alt="" aria-hidden="true" />
                              )}
                            </div>
                          )}
                        </>
                      )}
                      <div className="flex flex-col items-start max-w-[155px]">
                        <label
                          title={
                            isLogin
                              ? (userData?.data?.accountData?.first_name ||
                                  "") +
                                " " +
                                (userData?.data?.accountData?.last_name || "")
                              : "--"
                          }
                          className="max-w-[155px] text-txtPrimary font-medium leading-5 text-sm truncate overflow-hidden whitespace-nowrap text-ellipsis"
                        >
                          {isLogin
                            ? (userData?.data?.accountData?.first_name || "") +
                              " " +
                              (userData?.data?.accountData?.last_name || "")
                            : "--"}
                        </label>
                        <div
                          title={userData?.data?.accountData?.accountid}
                          className="max-w-[155px] flex items-center gap-1 text-sm leading-5 font-normal text-txtSecondary truncate overflow-hidden whitespace-nowrap text-ellipsis"
                        >
                          <span>ID</span>
                          <span>
                            {isLogin
                              ? userData?.data?.accountData?.accountid
                              : "--"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Menu.Item>
                  <Menu.Item
                    as="div"
                    className="py-1 px-[6px] cursor-pointer border-b border-strokePrimary-200 hover:!bg-bgHover"
                    data-testid={tid("sidebar-profile-link")}
                    aria-label="My profile"
                    onClick={() => handleItemClick(`/${cluster}/my-profile`)}
                  >
                    {({ active }) => (
                      <div className="py-[9px] px-[10px] flex items-center gap-2">
                        <UserIcon aria-hidden="true" />
                        <div className="text-sm leading-[20px] text-txtPrimary">
                          My profile
                        </div>
                      </div>
                    )}
                  </Menu.Item>
                  <Menu.Item
                    as="div"
                    className="py-1 px-[6px] cursor-pointer hover:!bg-bgHover"
                    data-testid={tid("sidebar-logout-button")}
                    aria-label="Log out"
                    onClick={() => {
                      handleLogOut();
                      setRotate(false);
                    }}
                  >
                    {({ active }) => (
                      <div className="py-[9px] px-[10px] flex items-center gap-2">
                        <LogOutIcon aria-hidden="true" />
                        <div className="text-sm leading-[20px] text-txtPrimary">
                          Log out
                        </div>
                      </div>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        ) : (
          <div
            className={`w-full transition-all duration-300 ease-in-out flex items-center gap-3 justify-between flex-shrink-0 relative pt-4 pb-6 px-3 border-t border-strokePrimary`}
          >
            <div
              className="px-[10px] flex items-center gap-2 cursor-pointer"
              onClick={handleLogOut}
              role="button"
              tabIndex={0}
              data-testid={tid("sidebar-login-button")}
              aria-label="Log in"
            >
              <LogOutIcon
                width="26"
                height="26"
                className="rotate-[180deg]"
                aria-hidden="true"
              />
              {!isSmallSidebar && (
                <div className="text-md leading-[20px] text-txtPrimary">
                  Login
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileButton;
